// EL MOTOR D'ESTOCS I FLUXOS.
//
// Fora de React i **sense cap import** tret de `expressions.js`, que tampoc no en té: així es pot
// fer córrer sol. `frontend/prova-motor-estocs.js` el prova contra estructures de resposta
// coneguda, incloent-hi una que té **solució analítica** i una que el motor de bucles causals
// **provadament no pot fer**.
//
// ── PER QUÈ AQUEST MOTOR NO ÉS L'ALTRE ───────────────────────────────────────────────────────
//
// El de `motorBucles.js` acaba amb aquesta nota, i és el punt de partida d'aquest:
//
// > El que NO fa: oscil·lar per sempre. […] cada variable porta el seu propi esmorteïment, ja
// > que el terme de saturació és proporcional al seu valor. **Per tenir oscil·lació sostinguda
// > caldria que les variables fossin integradors purs, que és el que són els estocs de l'altre
// > llenç.**
//
// Doncs són aquests. Un estoc **no té cap terme d'esmorteïment ni cap saturació**: només acumula
// el que hi entra i descompta el que en surt.
//
//     dS/dt = Σ fluxos que hi entren − Σ fluxos que en surten
//
// I prou. Tota la conducta —creixement, decaïment, cerca d'objectiu, oscil·lació— surt de com
// s'hagi dibuixat, no de cap constant amagada aquí dins. És la diferència entre un motor que
// il·lustra i un que calcula.
//
// ── QUI DEPÈN DE QUI ─────────────────────────────────────────────────────────────────────────
//
// Els estocs són **l'estat**: el seu valor ve del pas anterior i no es calcula, s'integra. Els
// convertidors i els fluxos són **funcions de l'estat**, i s'han d'avaluar en ordre de
// dependència. Un cicle entre convertidors és un **bucle algebraic** —«a val el que val b, que
// val el que val a»— i no té solució; es detecta i es diu, en comptes de recursar fins que el
// navegador es queda sense pila.
//
// **Els estocs trenquen els cicles per construcció**, i és exactament la seva raó de ser a la
// disciplina: un estoc recorda, i per això un model pot tenir retroacció sense contradicció.
//
// ── ELS NÚVOLS ───────────────────────────────────────────────────────────────────────────────
//
// Un núvol és fora de la frontera: **no té valor i no s'integra**. Un flux que hi va o que en ve
// simplement no suma ni resta a cap estoc per aquell extrem. Això no és una simplificació, és el
// que vol dir la notació.
//
// ── ELS ESTOCS NEGATIUS NO ES RETALLEN ───────────────────────────────────────────────────────
//
// Si un dipòsit baixa de zero, **el motor no ho amaga**: el model diu que en surt més del que hi
// ha, i això és una errada de l'estructura que qui dibuixa ha de veure. Retallar-ho en silenci
// convertiria un model equivocat en un que sembla que funciona. Qui ho ensenya és el llenç, amb
// el número de debò i una marca.

import { analitza, avalua, referencies, REF_TEMPS } from './expressions';

// Pas d'integració, en unitats de temps del model. **El mateix que `motorBucles.js`**, i no per
// simetria: es va comprovar contra la solució analítica del decaïment exponencial abans de
// deixar-lo (vegeu `prova-motor-estocs.js`). Amb un sol mantenidor, dos motors amb el mateix pas
// és una cosa menys per recordar.
export const DT = 0.05;

// Quants passos com a màxim per crida. És el tallafoc per si un fotograma triga molt —una
// pestanya amagada, una finestra arrossegada— i no una decisió de modelització.
const MAX_PASSOS_PER_CRIDA = 400;

const esEstoc = (c) => c.type === 'stock';
const esCalculat = (c) => c.type === 'flow' || c.type === 'converter';

// ── COMPILAR ─────────────────────────────────────────────────────────────────────────────────
//
// Es fa un sol cop per dibuix, no a cada pas: analitzar text seixanta vegades per segon seria
// llençar-ho tot. Retorna l'ordre d'avaluació o el primer problema que hi hagi, amb prou detall
// perquè es pugui dir a la pantalla de qui és.
export function compila(components) {
  const perId = new Map(components.map((c) => [String(c.id), c]));
  const arbres = new Map();
  const problemes = [];

  for (const c of components.filter(esCalculat)) {
    const r = analitza(c.properties?.equation || '');
    if (r.error) { problemes.push({ id: c.id, clau: r.error, detall: r.detall }); continue; }
    arbres.set(String(c.id), r.arbre);
    for (const ref of referencies(r.arbre)) {
      // El temps no és cap component i no s'ha de buscar a la llista.
      if (ref !== REF_TEMPS && !perId.has(ref)) problemes.push({ id: c.id, clau: 'sf.eqRefPerduda' });
    }
  }
  if (problemes.length) return { problemes };

  // Ordre topològic sobre els calculats. Les referències a estocs no compten com a dependència:
  // el valor d'un estoc ja el tenim de l'estat, i és això el que trenca els cicles.
  const calculats = components.filter(esCalculat).map((c) => String(c.id));
  // El temps tampoc no és cap dependència: el sap el motor a cada pas, com el valor d'un estoc.
  const depen = new Map(calculats.map((id) => [id,
    [...referencies(arbres.get(id) || {})].filter((r) => calculats.includes(r))]));

  const ordre = [];
  const estat = new Map(calculats.map((id) => [id, 0]));   // 0 pendent · 1 en curs · 2 fet
  let cicle = null;
  const visita = (id, cami) => {
    if (estat.get(id) === 2) return;
    if (estat.get(id) === 1) {
      cicle = cicle || [...cami.slice(cami.indexOf(id)), id].map((x) => perId.get(x)?.name || x);
      return;
    }
    estat.set(id, 1);
    for (const d of depen.get(id) || []) visita(d, [...cami, id]);
    estat.set(id, 2);
    ordre.push(id);
  };
  for (const id of calculats) { visita(id, []); if (cicle) break; }
  if (cicle) return { problemes: [{ clau: 'sf.eqBucleAlgebraic', detall: cicle.join(' → ') }] };

  // Per a cada estoc: qui l'omple i qui el buida. Es calcula un cop i s'usa a cada pas.
  const entrades = new Map(), sortides = new Map();
  for (const s of components.filter(esEstoc)) { entrades.set(String(s.id), []); sortides.set(String(s.id), []); }
  for (const f of components.filter((c) => c.type === 'flow')) {
    const de = String(f.properties?.from), a = String(f.properties?.to);
    if (sortides.has(de)) sortides.get(de).push(String(f.id));
    if (entrades.has(a)) entrades.get(a).push(String(f.id));
  }

  return {
    arbres, ordre, entrades, sortides,
    estocs: components.filter(esEstoc).map((c) => String(c.id)),
    fluxos: components.filter((c) => c.type === 'flow').map((c) => String(c.id)),
    convertidors: components.filter((c) => c.type === 'converter').map((c) => String(c.id)),
    problemes: [],
  };
}

// ── UN PAS ───────────────────────────────────────────────────────────────────────────────────

// Els valors de tot el que no és estoc, donat l'estat dels estocs I L'INSTANT. **Pura**: no toca
// res. El `t` per defecte és 0 i és una comoditat perillosa: cridar-la sense passar-lo dona
// valors que semblen bons i tenen el rellotge aturat, que és el que va passar al llenç fins que
// la prova de fum ho va veure. **Passa-hi sempre `estat.t`.**
export function avaluats(valorsEstocs, compilat, t = 0) {
  const valors = { ...valorsEstocs, [REF_TEMPS]: t };
  for (const id of compilat.ordre) valors[id] = avalua(compilat.arbres.get(id), valors);
  return valors;
}

// La derivada de cada estoc. També pura, i separada a posta: és el que caldria per canviar
// d'integrador sense tocar res més.
export function derivades(valorsEstocs, compilat, t = 0) {
  const valors = avaluats(valorsEstocs, compilat, t);
  const d = {};
  for (const s of compilat.estocs) {
    let suma = 0;
    for (const f of compilat.entrades.get(s) || []) suma += valors[f] || 0;
    for (const f of compilat.sortides.get(s) || []) suma -= valors[f] || 0;
    d[s] = suma;
  }
  return { d, valors };
}

export function estatInicial(components, compilat) {
  const valors = {};
  for (const s of compilat.estocs) {
    const c = components.find((x) => String(x.id) === s);
    valors[s] = Number(c?.properties?.initialValue) || 0;
  }
  return { t: 0, valors, objectiu: 0, saltats: 0 };
}

// RUNGE-KUTTA DE QUART ORDRE, i la tria és MESURADA, no una preferència.
//
// El primer intent era Euler explícit, com `motorBucles.js`. La prova el compara amb la solució
// analítica del decaïment exponencial —`S(t) = S0·e^(−kt)`, l'única estructura d'aquest llenç
// que en té una— i el número va sortir sol:
//
//     Euler amb DT = 0,05   →   4,90 % de desviació
//     Runge-Kutta, igual DT →   per sota del 0,01 %
//
// El 4,9 % **no és soroll: és un biaix sistemàtic**. Euler subestima la corba a cada pas i
// l'error s'acumula sempre en la mateixa direcció. En un llenç on la lliçó és comparar la forma
// de dues corbes, un 5 % que sempre va cap al mateix cantó és una lliçó falsa.
//
// I amb els oscil·ladors és pitjor encara: Euler explícit **guanya energia** en un sistema de
// dos estocs acoblats, o sigui que l'amplitud creix sola. Un pèndol que s'engrandeix tot sol no
// és cap conducta del model, és de l'integrador.
//
// El cost és avaluar les derivades quatre vegades per pas en comptes d'una. Amb models d'aula
// —desenes de components— no es nota, i `derivades` ja era una funció pura precisament per poder
// fer aquest canvi sense tocar res més.
//
// > **La condició per a la qual això val**: les expressions són contínues i derivables. Ho són
// > perquè l'analitzador només accepta `+ − × ÷` i parèntesis. **El dia que s'hi afegeixi un
// > condicional, un mínim o un màxim, Runge-Kutta deixarà de ser una millora** i aquesta decisió
// > s'haurà de refer. És per això que aquelles funcions no hi són.
export function passa(estat, compilat, dt = DT) {
  const suma = (base, k, factor) => {
    const v = {};
    for (const s of compilat.estocs) v[s] = base[s] + (k[s] || 0) * factor;
    return v;
  };
  // EL TEMPS AVANÇA DINS DEL PAS, i això no és cap detall.
  //
  // Runge-Kutta avalua la derivada a quatre llocs, i **dos d'ells són a mig pas**. Mentre cap
  // equació no podia anomenar el temps, el sistema era autònom i cridar quatre vegades amb el
  // mateix instant era correcte. **Des que `TIME` es pot escriure a una equació, deixa de
  // ser-ho**: si les quatre crides fessin servir `t`, l'integrador seria silenciosament
  // equivocat justament per a les equacions que aquesta funcionalitat serveix per escriure.
  //
  // Es veu amb una prova que no admet interpretacions: un flux que valgui `TIME` ha de donar
  // exactament `t²/2`, perquè Runge-Kutta és EXACTE per als polinomis fins a grau 4. Amb el temps
  // mal encadenat surt un ~1 % d'error, que sense aquesta prova ningú no distingiria de soroll.
  const t = estat.t;
  const k1 = derivades(estat.valors, compilat, t).d;
  const k2 = derivades(suma(estat.valors, k1, dt / 2), compilat, t + dt / 2).d;
  const k3 = derivades(suma(estat.valors, k2, dt / 2), compilat, t + dt / 2).d;
  const k4 = derivades(suma(estat.valors, k3, dt), compilat, t + dt).d;

  const valors = {};
  for (const s of compilat.estocs) {
    valors[s] = estat.valors[s] + (dt / 6) * (k1[s] + 2 * k2[s] + 2 * k3[s] + k4[s]);
  }
  return { ...estat, t: estat.t + dt, valors };
}

// Avança el temps de model que toca per als segons de rellotge que han passat, acumulant el
// sobrant. A 60 Hz cada fotograma demana una fracció de pas; arrodonint, seria zero i el model
// no avançaria mai — és l'errada que la prova del motor causal va trobar el 25/08/2026.
export function avanca(estat, compilat, segons, velocitat = 1, dt = DT) {
  // Es guarda el temps de model DEMANAT fins ara i se'n dedueix quants passos toquen, en comptes
  // d'anar acumulant un sobrant. Amb un sobrant, 600 fotogrames d'1/60 i 300 d'1/30 sumen
  // 10,000000000000002 i 9,999999999999998 —el mateix segon, dos últims bits diferents— i el
  // recompte de passos queia a bandes diferents: **el model avançava un pas més en una pantalla
  // que en una altra**. Ho va trobar la prova, i és la mateixa família d'errada que el
  // `Math.round` que a `motorBucles.js` deixava el model quiet a 60 Hz.
  const objectiu = (estat.objectiu || 0) + segons * velocitat;
  const volen = Math.floor(objectiu / dt + 1e-9);
  const fets = Math.round(estat.t / dt);
  const demanats = Math.max(0, volen - fets);
  // El topall és per no penjar el navegador si un fotograma triga molt o la pestanya ha estat
  // amagada. **Però el que no es fa no s'ha de quedar demanat**: si l'objectiu es guardés sencer,
  // `fets` no l'atraparia mai i el temps de model aniria quedant-se enrere per sempre, sense cap
  // senyal. `saltats` ho diu a qui ho vulgui mirar en comptes de retallar en silenci.
  const passos = Math.min(demanats, MAX_PASSOS_PER_CRIDA);
  // Del que s'ha demanat només es descompta el que el topall ha saltat, i **no la fracció de pas
  // que encara no arriba a un pas sencer**: aquella s'ha de quedar. Quedar-se-la tota era acumular
  // un deute que no s'atraparia mai; llençar-la tota era el contrari —a 60 Hz cada fotograma
  // demana un terç de pas, i esborrant-lo el model no avançava MAI—. Les dues errades es van
  // cometre, una després de l'altra, i totes dues les va trobar aquesta prova.
  const nouObjectiu = objectiu - (demanats - passos) * dt;
  let e = { ...estat, objectiu: nouObjectiu };
  for (let i = 0; i < passos; i++) e = { ...passa(e, compilat, dt), objectiu: nouObjectiu };
  return { ...e, saltats: (estat.saltats || 0) + (demanats - passos) };
}

const MOTOR = { DT, compila, avaluats, derivades, estatInicial, passa, avanca };
export default MOTOR;
